import type { MouseEvent } from 'react';

import classNames from 'classnames';

import { Dropdown, DropdownItem } from '@uikit/components/Dropdown';
import ExternalLink from '@uikit/components/ExternalLink/ExternalLink';
import { MastercardBrandColors } from '@uikit/icons/MastercardBrandColors';
import { VisaCard } from '@uikit/icons/VisaCard';

import SwitchingIcon from '@shared/common/components/SwitchingIcon';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { useBanxaModal } from '@shared/common/providers/GlobalModalsProvider';
import { sqrTokenSourcesInOrder } from '@shared/constants/sqrTokenSources';

import { useFeatureTogglesQuery } from '@query/featureToggles';

interface TokenProvidersProps {
  className?: string;
}

const TokenProviders = ({ className }: TokenProvidersProps) => {
  const { data: featureToggles, isPending: areFeatureTogglesLoading } = useFeatureTogglesQuery();

  const modal = useBanxaModal();

  const { userEvmWallet } = useAuth();

  const handleClick = () => {
    modal.open({
      walletAddress: userEvmWallet?.attributes.address,
      productName: 'Magic Launchpad',
    });
  };

  const sqrTokenSourcesIcons = sqrTokenSourcesInOrder.map(({ Icon }) => Icon);

  return (
    <div suppressHydrationWarning className={classNames('flex gap-2 max-sm:flex-col', className)}>
      <Dropdown
        shouldMatchTriggerWidth
        trigger={({ triggerProps: { onClick, ...triggerProps } }) => {
          const handleDropdownClick = (event: MouseEvent<HTMLButtonElement>) => {
            if (onClick && typeof onClick === 'function') {
              onClick(event);
            }
          };

          return (
            <button
              type="button"
              onClick={handleDropdownClick}
              className="flex-1 text-sm flex justify-center items-center w-full bg-primary-100 rounded-lg py-3 px-2 gap-2 hover:bg-primary-200 transition-colors"
              {...triggerProps}
            >
              <SwitchingIcon
                // disabling animation until we load feature toggles to animate 2 icons at the same time
                disabled={areFeatureTogglesLoading}
                icons={sqrTokenSourcesIcons}
                className="size-6"
                iconContainerClassName="rounded-full bg-neutral-50 p-1"
              />

              <span className="flex flex-col">
                <span className="font-semibold">Buy SQR with Crypto</span>
              </span>
            </button>
          );
        }}
      >
        {sqrTokenSourcesInOrder.map(({ label, Icon, href }) => {
          return (
            <DropdownItem
              Component={ExternalLink}
              key={label}
              className="flex items-center gap-2"
              label={label}
              href={href}
            >
              <Icon className="icon" />
              {label}
            </DropdownItem>
          );
        })}
      </Dropdown>

      {featureToggles?.banxaSqrTokenSource && (
        <button
          type="button"
          className="flex-1 text-sm flex justify-center items-center w-full bg-primary-100 rounded-lg py-3 px-2 gap-2 hover:bg-primary-200 transition-colors"
          onClick={handleClick}
        >
          <SwitchingIcon
            icons={[VisaCard, MastercardBrandColors]}
            className="size-6"
            iconContainerClassName="rounded-full bg-neutral-50 p-1"
          />

          <span className="flex flex-col">
            <span className="font-semibold">Buy SQR with Card</span>
          </span>
        </button>
      )}
    </div>
  );
};

export default TokenProviders;
